import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Buildings, GridFour, Cube, Hash } from "@phosphor-icons/react";

const TYPES = ["LOBBY", "SECTOR", "BLOCK", "BUILDING", "FLAT"];
const LABELS = {
  LOBBY: "Lobby",
  SECTOR: "Sector (Brand)",
  BLOCK: "Block (Category)",
  BUILDING: "Building (Product)",
  FLAT: "Flat (Attribute)",
};
const PARENT_OF = { SECTOR: "LOBBY", BLOCK: "SECTOR", BUILDING: "BLOCK", FLAT: "BUILDING" };
const ICONS = { LOBBY: Buildings, SECTOR: GridFour, BLOCK: Cube, BUILDING: Buildings, FLAT: Hash };

export default function LocationManager() {
  const [locs, setLocs] = useState([]);
  const [type, setType] = useState("SECTOR");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [parentId, setParentId] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/locations");
    setLocs(r.data);
  };
  useEffect(() => {
    load();
  }, []);

  const parents = type === "LOBBY" ? [] : locs.filter((l) => l.type === PARENT_OF[type]);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/locations", {
        type,
        name,
        code,
        parent_id: type === "LOBBY" ? null : parentId,
        description,
      });
      toast.success(`${LABELS[type]} added`);
      setName("");
      setCode("");
      setDescription("");
      setParentId("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, n) => {
    if (!window.confirm(`Delete ${n}? Children/entries must be removed first.`)) return;
    try {
      await api.delete(`/locations/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-8" data-testid="location-manager-page">
      <div>
        <div className="mono-label text-orange-600 mb-2">// architectural map</div>
        <h1 className="font-display text-4xl md:text-5xl text-slate-900 tracking-tight leading-tight">
          Complex Map
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Build the residential structure of the AiVerse: Lobby → Sector → Block → Building → Flat.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <form onSubmit={create} className="border border-slate-200 bg-white p-6 space-y-4 lg:col-span-1" data-testid="location-form">
          <div className="mono-label">New address</div>
          <div>
            <label className="mono-label block mb-1">Type</label>
            <select
              data-testid="loc-type-select"
              value={type}
              onChange={(e) => {
                setType(e.target.value);
                setParentId("");
              }}
              className="w-full border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-slate-900"
              style={{ borderRadius: 2 }}
            >
              {TYPES.map((t) => (
                <option key={t} value={t}>
                  {LABELS[t]}
                </option>
              ))}
            </select>
          </div>
          {type !== "LOBBY" && (
            <div>
              <label className="mono-label block mb-1">Parent ({LABELS[PARENT_OF[type]]})</label>
              <select
                data-testid="loc-parent-select"
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                required
                className="w-full border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-slate-900"
                style={{ borderRadius: 2 }}
              >
                <option value="">— select —</option>
                {parents.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="mono-label block mb-1">Name</label>
            <input
              data-testid="loc-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900"
              style={{ borderRadius: 2 }}
            />
          </div>
          <div>
            <label className="mono-label block mb-1">Code (optional)</label>
            <input
              data-testid="loc-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 font-mono text-sm outline-none focus:border-slate-900"
              style={{ borderRadius: 2 }}
            />
          </div>
          <div>
            <label className="mono-label block mb-1">Description</label>
            <textarea
              data-testid="loc-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-slate-900 resize-none"
              style={{ borderRadius: 2 }}
            />
          </div>
          <button type="submit" disabled={busy} className="w-full btn-sharp btn-primary disabled:opacity-50" data-testid="create-location-btn">
            <Plus size={16} weight="bold" />
            {busy ? "Adding…" : "Add address"}
          </button>
        </form>

        <div className="lg:col-span-2 space-y-3">
          {TYPES.map((t) => {
            const items = locs.filter((l) => l.type === t);
            const Icon = ICONS[t];
            return (
              <div key={t} className="border border-slate-200 bg-white">
                <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
                  <Icon size={16} className="text-slate-700" />
                  <div className="mono-label">{LABELS[t]}</div>
                  <span className="font-mono text-xs px-2 py-0.5 bg-slate-100 border border-slate-200 ml-auto">
                    {items.length}
                  </span>
                </div>
                {items.length === 0 ? (
                  <div className="px-5 py-3 text-sm text-slate-400">— none —</div>
                ) : (
                  <div>
                    {items.map((l) => (
                      <div
                        key={l.id}
                        className="flex items-center justify-between border-b border-slate-100 px-5 py-2 last:border-b-0"
                        data-testid={`loc-row-${l.id}`}
                      >
                        <div>
                          <div className="text-sm text-slate-900">{l.name}</div>
                          {l.code && <div className="font-mono text-xs text-slate-500">{l.code}</div>}
                        </div>
                        <button onClick={() => remove(l.id, l.name)} className="text-slate-400 hover:text-orange-600" data-testid={`delete-loc-${l.id}`}>
                          <Trash size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
